import { useMyTasksToday } from '../hooks/useTasks'
import { TaskItem } from './TaskItem'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Link } from 'react-router-dom'
import { CheckSquare, AlertCircle, ArrowRight, Loader2 } from 'lucide-react'

/**
 * MyTasksTodayWidget Component
 *
 * Dashboard widget showing tasks due today or overdue.
 * Quick access to complete tasks without leaving the dashboard.
 */
export function MyTasksTodayWidget() {
  const { data, isLoading, error } = useMyTasksToday()

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            My Tasks Today
            {data && data.counts.overdue > 0 && (
              <Badge variant="destructive" className="ml-2">
                <AlertCircle className="h-3 w-3 mr-1" />
                {data.counts.overdue} overdue
              </Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard/tasks" className="text-xs">
              View all
              <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="text-sm text-destructive py-4 text-center">
            Failed to load tasks
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            {data.tasks.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                No tasks due today. You're all caught up!
              </div>
            ) : (
              <div className="space-y-1">
                {data.tasks.slice(0, 5).map((task) => (
                  <TaskItem key={task.id} task={task} compact />
                ))}
                {data.tasks.length > 5 && (
                  <div className="pt-2">
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <Link to="/dashboard/tasks">
                        View {data.tasks.length - 5} more tasks
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
